import { useQuery, useMutation, UseQueryResult } from "@tanstack/react-query";
import { useMemo } from "react";
import UserRepository from "./user.repository";
import { User, ParsedUser } from "./user.model";
import { transformUser } from "../../../../../src/domains/shared/utils/transformedUserInfo";

export const useGetUser = (): Omit<UseQueryResult<ParsedUser>, "data"> & {
  data: ParsedUser | undefined;
} => {
  const queryInfo = useQuery<User>({
    queryKey: ["getUser"],
    queryFn: () => UserRepository.getUser(),
  });
  return {
    ...queryInfo,
    data: useMemo(() => {
      if (queryInfo.data) {
        return transformUser(queryInfo.data);
      }
      return undefined;
    }, [queryInfo.data]),
  };
};

/**
 * 내 프로젝트 역할 쿼리 키. 접두 `PROJECT_USER_ROLE_QUERY_KEY` 로 무효화하면 프로젝트별(`useIsMyProject`)과
 * 전체(`useMyProjectRoles`)가 함께 다시 읽힌다 — 소유권 이전처럼 역할이 바뀌는 쓰기가 쓴다.
 */
export const PROJECT_USER_ROLE_QUERY_KEY = ["projectUserRole"] as const;
export const projectUserRoleQueryKey = (projectId: number) =>
  [...PROJECT_USER_ROLE_QUERY_KEY, projectId] as const;

export const useIsMyProject = (projectId: number) => {
  return useQuery({
    queryKey: projectUserRoleQueryKey(projectId),
    queryFn: UserRepository.getUserWithProjects,
    enabled: !!projectId,
    select(data) {
      const myProject = data.project_user.find(
        (user) => user.project_id === projectId
      );
      if (!myProject) return "none";
      return myProject.role;
    },
  });
};

/** 내가 속한 프로젝트마다의 역할 — 여러 프로젝트 중 하나의 역할을 그때그때 봐야 하는 자리(프로젝트 목록 표)가 쓴다. */
export const useMyProjectRoles = () => {
  return useQuery({
    queryKey: PROJECT_USER_ROLE_QUERY_KEY,
    queryFn: UserRepository.getUserWithProjects,
    select: (data) => data.project_user,
  });
};

export const usePatchUser = () => {
  return useMutation({
    mutationKey: ["patchUser"],
    mutationFn: (data: ParsedUser) => UserRepository.patchUser(data),
  });
};

export const useDeleteUser = () => {
  return useMutation({
    mutationKey: ["deleteUser"],
    mutationFn: () => UserRepository.deleteUser(),
  });
};
